import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { 
  Heart, 
  MoreVertical, 
  Check, 
  X, 
  FileText, 
  Trash2, 
  MessageSquare,
  Star,
  Download,
  User
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImageCard({ image, selected, onSelect, onUpdate }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await axios.get(`${API}/users`, { withCredentials: true });
        setUsers(data);
      } catch (err) {
        console.error("Failed to fetch users");
      }
    };
    fetchUsers();
  }, []);

  const updateStatus = async (status) => {
    try {
      await axios.put(`${API}/images/${image.id}`, { status }, { withCredentials: true });
      toast.success(`Image ${status}`);
      onUpdate();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const toggleFavorite = async () => {
    try {
      await axios.put(`${API}/images/${image.id}`, { is_favorite: !image.is_favorite }, { withCredentials: true });
      toast.success(image.is_favorite ? "Removed from favorites" : "Added to favorites");
      onUpdate();
    } catch (err) {
      toast.error("Failed to update favorite");
    }
  };

  const updateRating = async (rating) => {
    try {
      await axios.put(`${API}/images/${image.id}`, { rating }, { withCredentials: true });
      toast.success("Rating updated");
      onUpdate();
    } catch (err) {
      toast.error("Failed to update rating");
    }
  };

  const updateAssignment = async (userId) => {
    try {
      await axios.put(`${API}/images/${image.id}`, { assigned_to: userId }, { withCredentials: true });
      toast.success("Assignment updated");
      onUpdate();
    } catch (err) {
      toast.error("Failed to update assignment");
    }
  };

  const deleteImage = async () => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await axios.delete(`${API}/images/${image.id}`, { withCredentials: true });
      toast.success("Image deleted");
      onUpdate();
    } catch (err) {
      toast.error("Failed to delete image");
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/images/${image.id}/comments`, { text: comment }, { withCredentials: true });
      toast.success("Comment added");
      setComment("");
      onUpdate();
    } catch (err) {
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = image.image_data;
    link.download = `tshirt-design-${image.id}.png`;
    link.click();
  };

  const statusBadgeClass = {
    draft: "status-draft",
    approved: "status-approved",
    rejected: "status-rejected"
  };

  return (
    <>
      <div 
        className="border border-gray-200 hover:border-black transition-colors group relative"
        data-testid={`image-card-${image.id}`}
      >
        {/* Selection Checkbox */}
        <div className="absolute top-2 left-2 z-10">
          <Checkbox
            checked={selected}
            onCheckedChange={onSelect}
            className="bg-white border-gray-400"
            data-testid={`select-image-${image.id}`}
          />
        </div>

        {/* Favorite Button */}
        <button
          onClick={toggleFavorite}
          className="absolute top-2 right-2 z-10 p-1 bg-white border border-gray-200 hover:border-black transition-colors"
          data-testid={`favorite-${image.id}`}
        >
          <Heart 
            size={16} 
            className={image.is_favorite ? "fill-pink-500 text-pink-500" : "text-gray-400"} 
          />
        </button>

        {/* Image */}
        <button
          onClick={() => setDetailOpen(true)}
          className="w-full aspect-square bg-gray-100 cursor-pointer"
        >
          <img
            src={image.image_data}
            alt={image.prompt}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </button>

        {/* Info */}
        <div className="p-3 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className={`px-2 py-0.5 text-xs uppercase tracking-wider ${statusBadgeClass[image.status]}`}>
              {image.status}
            </span>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1 hover:bg-gray-100" data-testid={`actions-${image.id}`}>
                  <MoreVertical size={16} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => updateStatus("approved")} data-testid={`approve-${image.id}`}>
                  <Check size={14} className="mr-2 text-green-600" />
                  Approve
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateStatus("rejected")} data-testid={`reject-${image.id}`}>
                  <X size={14} className="mr-2 text-red-600" />
                  Reject
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateStatus("draft")} data-testid={`draft-${image.id}`}>
                  <FileText size={14} className="mr-2 text-yellow-600" />
                  Set Draft
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDownload} data-testid={`download-${image.id}`}>
                  <Download size={14} className="mr-2" />
                  Download
                </DropdownMenuItem>
                <DropdownMenuItem onClick={deleteImage} className="text-red-600" data-testid={`delete-${image.id}`}>
                  <Trash2 size={14} className="mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <p className="text-xs text-gray-500 line-clamp-2">{image.prompt}</p>
          
          {/* Rating */}
          <div className="flex items-center gap-1 mt-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => updateRating(star)}
                className="p-0.5"
                data-testid={`rate-${image.id}-${star}`}
              >
                <Star
                  size={14}
                  className={star <= (image.rating || 0) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}
                />
              </button>
            ))}
          </div>
          
          {/* Assignment badge */}
          {image.assigned_user_name && (
            <div className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs">
              <User size={10} />
              {image.assigned_user_name}
            </div>
          )}
          
          {/* Comments indicator */}
          {image.comments?.length > 0 && (
            <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
              <MessageSquare size={12} />
              {image.comments.length}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-black">Image Details</DialogTitle>
          </DialogHeader>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Image */}
            <div>
              <img
                src={image.image_data}
                alt={image.prompt}
                className="w-full border border-gray-200"
              />
              <Button
                onClick={handleDownload}
                variant="outline"
                className="w-full mt-3 rounded-none"
              >
                <Download size={16} className="mr-2" />
                Download Image
              </Button>
            </div>

            {/* Details */}
            <div className="space-y-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Title</p>
                <p className="text-sm text-black">{image.title || image.prompt || "-"}</p>
              </div>

              {image.original_filename && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Original Filename</p>
                  <p className="text-sm text-gray-600 break-all">{image.original_filename}</p>
                </div>
              )}

              {!image.original_filename && image.prompt && image.prompt !== image.title && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Prompt</p>
                  <p className="text-sm text-gray-600">{image.prompt}</p>
                </div>
              )}

              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Status</p>
                <span className={`px-3 py-1 text-sm ${statusBadgeClass[image.status]}`}>
                  {image.status}
                </span>
              </div>

              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Rating</p>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => updateRating(star)}
                      className="p-1"
                    >
                      <Star
                        size={20}
                        className={star <= (image.rating || 0) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Assignment */}
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Assign To</p>
                <Select 
                  value={image.assigned_to || "unassigned"} 
                  onValueChange={(val) => updateAssignment(val === "unassigned" ? "" : val)}
                >
                  <SelectTrigger className="rounded-none border-gray-300 bg-white text-black" data-testid="assign-select">
                    <SelectValue placeholder="Select person" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Comments */}
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Comments</p>
                
                {image.comments?.length > 0 && (
                  <div className="space-y-2 mb-3 max-h-32 overflow-y-auto">
                    {image.comments.map((c, i) => (
                      <div key={i} className="p-2 bg-gray-50 border border-gray-200 text-sm">
                        <p className="font-medium text-xs text-black">{c.user_name}</p>
                        <p className="text-gray-600">{c.text}</p>
                      </div>
                    ))}
                  </div>
                )}

                <form onSubmit={addComment} className="flex gap-2">
                  <Textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="rounded-none border-gray-300 bg-white text-black resize-none text-sm"
                    rows={2}
                    data-testid="comment-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={submitting || !comment.trim()}
                    className="rounded-none bg-black text-white hover:bg-gray-800 self-end"
                    data-testid="submit-comment"
                  >
                    {submitting ? "..." : "Add"}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
